// One-click check-in for a repeat Day Care session (spec Q5), and the billing
// every admitted session needs.
//
// Every step calls the IPD / billing module's own exported function or
// endpoint; nothing here copies their logic. The order matters: the admission
// is linked to the session as soon as it exists, so a later failure
// (allocation, billing, package) leaves a linked session the board can finish
// with finishCheckIn() or startBilling(), never an orphan admission.

import axios from "axios";
import { BASE, fetchCheckinTemplate, linkAdmission } from "./daycareApi";
import { mapAdmissionDataToPatient } from "../../ipd/pages/patient-admission-ipd/utils/mapAdmissionDataToPatient";
import { submitIpdPatientRegistration } from "../../ipd/pages/patient-admission-ipd/api/registrationService";
import { assignPackage, ensureBillingSetup, getPackageQuickHeader } from "../../ipd-billing/package-billing-api";
import { getChargeEvents, postCharge } from "../../ipd-billing/billing-api";
import { startBedAccrual } from "../../ipd/api/autoChargeApi";

/** What billing a session needs: the unit's package, else the order's service. */
export function billingFor(session, unit) {
  return {
    sessionId: session.id,
    packageId: unit?.package_id || null,
    serviceId: session.billing_service_id || null,
  };
}

// The admission is created with its bed "reserved", then allocated on the same
// bed — the endpoint and body BedAllocation.js posts (its call is inline there,
// not exported). Tenant headers come from the global interceptor.
export async function allocateBed(admissionId, bedCode) {
  const res = await axios.post(
    `${BASE}ipd_bed_allocation/`,
    { admission_id: admissionId, old_bed_id: bedCode, new_bed_id: bedCode },
    { headers: { Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}` } },
  );
  return res?.data || {};
}

export async function checkIn(sessionId) {
  let template;
  try {
    template = await fetchCheckinTemplate(sessionId);
  } catch (err) {
    return { ok: false, stage: "template", message: err?.body?.message || err.message };
  }

  const mapped = mapAdmissionDataToPatient(template.admission_data, { patient_id: template.patient_id });
  const patient = {
    ...mapped,
    admission_id: null,
    encounter: {
      ...mapped.encounter,
      admissionType: "DAY_CARE",
      admittingDoctorId: template.admitting_doctor_id || mapped.encounter.admittingDoctorId,
      admittingDoctorName: "",
      wardNo: template.bed_type_code,
      bedId: template.bed_code,
      newBedStatus: "reserved",
    },
  };

  let admissionId;
  try {
    const result = await submitIpdPatientRegistration(patient, { selectedPatientIdForSearch: template.patient_id });
    admissionId = result.admission_id;
  } catch (err) {
    return { ok: false, stage: "admission", message: err.message };
  }

  const link = await linkAdmission(sessionId, admissionId);
  if (!link.ok) return { ok: false, stage: "link", message: link.message, admissionId };

  return finishCheckIn(admissionId, template.bed_code, {
    sessionId,
    packageId: template.package_id || null,
    serviceId: template.billing_service_id || null,
  });
}

// Everything after the admission exists — also what the board's "Finish
// check-in" runs for a session whose admission is not yet allocated.
export async function finishCheckIn(admissionId, bedCode, billing) {
  let allocated;
  try {
    allocated = await allocateBed(admissionId, bedCode);
  } catch (err) {
    allocated = { message: err.response?.data?.message || err.message };
  }
  if (allocated.status !== "Success") {
    return { ok: false, stage: "allocation", message: allocated.message || "Could not allocate the machine", admissionId };
  }
  const result = await startBilling(admissionId, billing);
  return result.ok ? { ...result, message: "Checked in" } : result;
}

// Billing for an allocated admission: the account, the bed-day accrual, then
// the session itself — through the unit's package when it has one, otherwise
// as the order's service. Every step is safe to repeat, so the board's
// "Complete billing" re-runs it after any failure.
export async function startBilling(admissionId, { sessionId, packageId, serviceId }) {
  let setup;
  try {
    setup = await ensureBillingSetup(admissionId);
  } catch (err) {
    return { ok: false, stage: "billing", message: err.message, admissionId };
  }
  await startBedAccrual(admissionId);

  if (packageId) {
    try {
      const assigned = await getPackageQuickHeader(setup.billingAccountId);
      if (!assigned) await assignPackage(admissionId, { package_id: packageId, create_initial_visit: true });
    } catch (err) {
      return { ok: false, stage: "package", message: err.message, admissionId };
    }
  } else if (serviceId) {
    const charged = await chargeSession(admissionId, serviceId, sessionId);
    if (!charged.ok) return { ok: false, stage: "charge", message: charged.message, admissionId };
  }
  return { ok: true, stage: "done", message: "Billing complete", admissionId };
}

/** True when the admission has a charge for the service that was not cancelled. */
export function hasOpenCharge(events, serviceId) {
  const list = events || [];
  const cancelled = new Set(
    list
      .filter((e) => String(e?.event_type || "").toUpperCase() === "CANCELLATION")
      .map((e) => e?.parent_event_id || e?.metadata?.original_charge_event_id)
      .filter(Boolean),
  );
  return list.some((e) =>
    String(e?.event_type || "CHARGE").toUpperCase() === "CHARGE"
    && !cancelled.has(e?.id || e?.charge_event_id)
    // Billing keeps the service id in the charge's metadata.
    && String(e?.metadata?.medical_service_id ?? e?.medical_service_id ?? "") === String(serviceId));
}

/** The charge's billing key: one per session, so billing itself refuses a second post. */
export function sessionChargeEventId(admissionId, sessionId) {
  return `EMR-IPD-${admissionId}-DAYCARE-${sessionId}`;
}

// One charge per session admission. The read skips a post already on the bill;
// the fixed external_event_id stops two users posting at the same moment.
// postCharge (not the bulk endpoint, which answers Success even when every row
// fails) so a refusal is reported.
export async function chargeSession(admissionId, serviceId, sessionId) {
  try {
    if (hasOpenCharge(await getChargeEvents(admissionId), serviceId)) {
      return { ok: true, message: "This session is already on the bill" };
    }
    await postCharge(admissionId, {
      medical_service_id: serviceId,
      quantity: 1,
      remarks: "Day care session",
      external_event_id: sessionChargeEventId(admissionId, sessionId),
    });
  } catch (err) {
    return { ok: false, message: err.message };
  }
  return { ok: true, message: "Session charged" };
}
